import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { trackPurchase } from "@/lib/analytics";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const paypalTxId = params.get("paypal_tx");
    const [status, setStatus] = useState("checking");
    const { refresh } = useAuth();

    useEffect(() => {
        // PayPal return flow — probe status first: recurring subscriptions activate via webhook,
        // one-time orders still need an explicit capture call.
        if (paypalTxId) {
            (async () => {
                let isRecurring = false;
                try {
                    const { data } = await http.get(`/paypal/status/${paypalTxId}`);
                    isRecurring = !!data.paypal_recurring;
                } catch {}
                if (!isRecurring) {
                    try {
                        await http.post("/paypal/capture", { tx_id: paypalTxId });
                    } catch {}
                }
                let tries = 0;
                const poll = async () => {
                    try {
                        const { data } = await http.get(`/paypal/status/${paypalTxId}`);
                        if (data.payment_status === "paid") {
                            setStatus("paid");
                            trackPurchase(paypalTxId, {
                                kind: data.kind,
                                item_name: data.item_name,
                                amount: data.amount,
                                lookup_key: data.lookup_key,
                            });
                            await refresh();
                            return;
                        }
                    } catch {}
                    tries += 1;
                    // Give subscription webhook up to ~30s to activate; capture is quicker
                    const cap = isRecurring ? 20 : 10;
                    if (tries < cap) setTimeout(poll, 1500);
                    else setStatus("timeout");
                };
                poll();
            })();
            return;
        }
        // Stripe return flow
        if (!sessionId) return;
        let tries = 0;
        const poll = async () => {
            try {
                const { data } = await http.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    trackPurchase(sessionId, {
                        kind: data.kind,
                        item_name: data.item_name,
                        amount: data.amount,
                        lookup_key: data.lookup_key,
                    });
                    await refresh();
                    return;
                }
                if (data.payment_status === "failed" || data.payment_status === "expired") {
                    setStatus("failed");
                    return;
                }
            } catch {}
            tries += 1;
            if (tries < 15) setTimeout(poll, 2000);
            else setStatus("timeout");
        };
        poll();
    }, [sessionId, paypalTxId, refresh]);

    return (
        <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="payment-success-page">
            <div className="text-center max-w-md">
                {status === "paid" && (
                    <>
                        <CheckCircle2 className="w-16 h-16 text-primary mx-auto mb-6" />
                        <h1 className="font-serif text-4xl mb-4">Paiement confirmé</h1>
                        <p className="text-muted-foreground mb-8">Bienvenue. Retrouvez tout dans votre espace personnel.</p>
                        <Link to="/espace">
                            <Button className="rounded-full h-12 px-6 bg-primary text-primary-foreground" data-testid="go-dashboard">Accéder à mon espace</Button>
                        </Link>
                    </>
                )}
                {status === "checking" && <p className="text-muted-foreground">Vérification du paiement…</p>}
                {status === "failed" && (
                    <>
                        <h1 className="font-serif text-4xl mb-4">Paiement non abouti</h1>
                        <p className="text-muted-foreground mb-6">Aucun montant n'a été prélevé.</p>
                        <Link to="/abonnements"><Button variant="outline" className="rounded-full">Réessayer</Button></Link>
                    </>
                )}
                {status === "timeout" && (
                    <>
                        <h1 className="font-serif text-4xl mb-4">Toujours en cours</h1>
                        <p className="text-muted-foreground mb-6">Votre paiement met plus de temps. Vérifiez votre espace dans quelques minutes.</p>
                        <Link to="/espace"><Button variant="outline" className="rounded-full">Aller à mon espace</Button></Link>
                    </>
                )}
            </div>
        </div>
    );
}
